import {
  ApartmentOutlined,
  ArrowLeftOutlined,
  ReloadOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import {
  Alert,
  Button,
  Card,
  Descriptions,
  Space,
  Spin,
  Tag,
  Tooltip,
  Typography,
  message,
} from 'antd';
import { useCallback, useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { ApiError } from '../api/errors';
import { getProject } from '../api/projects';
import type { ProjectDetail } from '../api/projects';
import { PermissionCodes } from '../auth/permissions';
import { useAuth } from '../auth/useAuth';
import { AppRoutes } from '../routes';
import { ProjectMemberCard } from './projects/ProjectMemberCard';
import { ProjectMilestoneCard } from './projects/ProjectMilestoneCard';
import { ProjectProgressCard } from './projects/ProjectProgressCard';
import { ProjectScheduleCard } from './projects/ProjectScheduleCard';
import { ProjectStatusCard } from './projects/ProjectStatusCard';
import { formatDate, healthTag, priorityTag, statusTag } from './projects/projectDisplay';

/**
 * Trang chi tiết một dự án.
 *
 * <b>Chia thành các thẻ theo quyền, không theo chủ đề.</b> Mỗi thẻ tương ứng với đúng một quyền của
 * máy chủ: sửa lịch trình, đổi trạng thái, cập nhật tiến độ, quản lý thành viên. Người không có
 * quyền nào thì thẻ đó hiện ở dạng chỉ đọc thay vì biến mất — biến mất sẽ làm người dùng tưởng dự án
 * không có thông tin đó.
 *
 * <b>Mọi phép sửa trả về toàn bộ dự án sau khi sửa</b>, nên trang cập nhật cả những trường do máy
 * chủ tính — mốc cam kết đang có hiệu lực, cờ quá hạn, cờ bảo hành sắp hết — mà không phải gọi thêm
 * một lượt đọc.
 */
export function ProjectDetailPage(): ReactNode {
  const { projectId } = useParams<{ projectId: string }>();
  const { hasPermission } = useAuth();
  const navigate = useNavigate();

  const [project, setProject] = useState<ProjectDetail | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    if (projectId === undefined) {
      return;
    }

    let isCurrent = true;

    async function load(identifier: string): Promise<void> {
      setErrorMessage(null);

      try {
        const loaded = await getProject(identifier);

        if (isCurrent) {
          setProject(loaded);
        }
      } catch (error) {
        if (!isCurrent) {
          return;
        }

        setErrorMessage(
          error instanceof ApiError ? error.message : 'Không tải được thông tin dự án.',
        );
      }
    }

    void load(projectId);

    return () => {
      isCurrent = false;
    };
  }, [projectId, reloadToken]);

  const reload = useCallback(() => { setReloadToken((token) => token + 1); }, []);

  /**
   * Nhận kết quả của một phép sửa và cập nhật trang.
   *
   * Dùng thẳng bản ghi máy chủ trả về thay vì gọi lại phép đọc: một lượt đi lại ít hơn, và quan
   * trọng hơn là không có khoảnh khắc nào trang hiển thị dữ liệu cũ sau khi lưu thành công.
   */
  const applyUpdate = useCallback((updated: ProjectDetail) => {
    setProject(updated);
    message.success('Đã lưu.');
  }, []);

  if (errorMessage !== null) {
    return (
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Button icon={<ArrowLeftOutlined />} onClick={() => { void navigate(AppRoutes.Project); }}>
          Về danh sách dự án
        </Button>
        <Alert type="error" showIcon message={errorMessage} />
      </Space>
    );
  }

  if (project === null) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <Space style={{ justifyContent: 'space-between', width: '100%' }} wrap>
        <Space direction="vertical" size={0}>
          <Space size={8} wrap>
            <Typography.Title level={4} style={{ margin: 0 }}>
              {project.code}
            </Typography.Title>
            {statusTag(project.status)}
            {priorityTag(project.priority)}
            {healthTag(project.health, project.healthReason)}
            {project.isInternal ? <Tag>Nội bộ</Tag> : null}
            {project.isOverdue ? (
              <Tooltip title="Đã quá mốc cam kết mà chưa kết thúc thực tế.">
                <Tag color="red" icon={<WarningOutlined />}>
                  Quá hạn
                </Tag>
              </Tooltip>
            ) : null}
            {project.isWarrantyExpiringSoon ? (
              <Tooltip
                title={`Bảo hành hết ngày ${formatDate(project.warrantyEndDate)}. Đây là lúc sắp lịch kiểm tra lần cuối và chào gói bảo trì tiếp theo.`}
              >
                <Tag color="gold">Bảo hành sắp hết</Tag>
              </Tooltip>
            ) : null}
          </Space>
          <Typography.Text>{project.name}</Typography.Text>
        </Space>
        <Space>
          <Button
            icon={<ArrowLeftOutlined />}
            onClick={() => { void navigate(AppRoutes.Project); }}
          >
            Danh sách
          </Button>
          <Button
            type="primary"
            icon={<ApartmentOutlined />}
            onClick={() => { void navigate(`${AppRoutes.Project}/${project.id}/cong-viec`); }}
          >
            Công việc
          </Button>
          <Button icon={<ReloadOutlined />} onClick={reload}>
            Tải lại
          </Button>
        </Space>
      </Space>

      <Card size="small" title="Thông tin chung">
        <Descriptions size="small" column={{ xs: 1, sm: 2, lg: 3 }} bordered>
          <Descriptions.Item label="Loại dự án">{project.projectTypeName}</Descriptions.Item>
          <Descriptions.Item label="Tên tiếng Anh">{project.nameEn ?? '—'}</Descriptions.Item>
          <Descriptions.Item label="Ngày nghiệm thu">
            {formatDate(project.acceptanceDate)}
          </Descriptions.Item>
          <Descriptions.Item label="Hạn bảo hành">
            {formatDate(project.warrantyEndDate)}
            {project.warrantyMonths === null ? '' : ` (${project.warrantyMonths.toString()} tháng)`}
          </Descriptions.Item>
          <Descriptions.Item label="Mô tả" span={3}>
            {project.description ?? '—'}
          </Descriptions.Item>
          <Descriptions.Item label="Phạm vi công việc" span={3}>
            {project.scopeSummary ?? '—'}
          </Descriptions.Item>
        </Descriptions>
      </Card>

      <ProjectProgressCard
        project={project}
        canEdit={hasPermission(PermissionCodes.ProjectUpdateProgress)}
        onUpdated={applyUpdate}
      />

      <ProjectStatusCard
        project={project}
        canChangeStatus={hasPermission(PermissionCodes.ProjectChangeStatus)}
        canRecordAcceptance={hasPermission(PermissionCodes.ProjectRecordAcceptance)}
        onUpdated={applyUpdate}
      />

      <ProjectScheduleCard
        project={project}
        canEdit={hasPermission(PermissionCodes.ProjectUpdateSchedule)}
        onUpdated={applyUpdate}
      />

      {/*
        Đường mốc đứng NGAY SAU lịch trình: hai thẻ nói về cùng một chuyện — hạn là ngày nào và đã
        trượt bao nhiêu — nên đặt cạnh nhau thì đọc một mạch được.
      */}
      <ProjectMilestoneCard
        projectId={project.id}
        canView={hasPermission(PermissionCodes.ProjectMilestoneView)}
        canManage={hasPermission(PermissionCodes.ProjectMilestoneManage)}
        canRecord={hasPermission(PermissionCodes.ProjectMilestoneRecord)}
      />

      <ProjectMemberCard
        projectId={project.id}
        canManage={hasPermission(PermissionCodes.ProjectMemberManage)}
        canView={hasPermission(PermissionCodes.ProjectMemberView)}
      />
    </Space>
  );
}
